import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { get } from "@/lib/api";
import { fmtDuree, fmtNum, fmtPct, TONE_TEXT, trsTone } from "@/lib/format";
import { Panel } from "@/components/common/Panel";
import { DataTable } from "@/components/common/DataTable";
import { LoadingState, ErrorState } from "@/components/common/States";
import { cn } from "@/lib/utils";

const AXIS = { fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" };
const QUART_STATUT = { termine: ["Terminé", "text-muted-foreground"], en_cours: ["En cours", "text-emerald-400"], a_venir: ["À venir", "text-slate-500"] };

const pct = (k, header) => ({ key: k, header, align: "right", render: (r) => <span className={cn(TONE_TEXT[trsTone(r[k])])}>{fmtPct(r[k])}</span> });

const columns = [
  { key: "quart", header: "Quart", render: (r) => <span className="font-semibold">{r.quart}</span> },
  { key: "horaires", header: "Horaires", mono: true, cellClassName: "text-muted-foreground" },
  { key: "statut_quart", header: "État", render: (r) => { const s = QUART_STATUT[r.statut_quart] || [r.statut_quart, ""]; return <span className={cn("font-mono text-[10.5px] uppercase", s[1])}>{s[0]}</span>; } },
  pct("trs", "TRS"), pct("disponibilite", "Dispo."), pct("performance", "Perf."), pct("qualite", "Qualité"),
  { key: "bonnes", header: "Bonnes", align: "right", render: (r) => fmtNum(r.bonnes) },
  { key: "rejets", header: "Rejets", align: "right", render: (r) => <span className={cn(r.rejets > 0 && "text-amber-400")}>{fmtNum(r.rejets)}</span> },
  { key: "objectif", header: "Objectif", align: "right", render: (r) => (r.objectif ? fmtNum(r.objectif) : "—") },
  { key: "avancement_pct", header: "Avancement", align: "right", render: (r) => r.avancement_pct == null ? "—" : (
    <div className="flex items-center gap-1.5 justify-end"><div className="w-12 h-1.5 bg-muted rounded-sm overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(r.avancement_pct, 100)}%` }} /></div><span className={cn(r.avancement_pct < 90 ? "text-amber-400" : "text-foreground")}>{fmtPct(r.avancement_pct, 0)}</span></div>) },
  { key: "nb_of", header: "OF", align: "right", render: (r) => r.nb_of ?? "—" },
  { key: "arrets_planifies_sec", header: "Arr. plan.", align: "right", render: (r) => fmtDuree(r.arrets_planifies_sec) },
  { key: "arrets_non_planifies_sec", header: "Arr. non plan.", align: "right", render: (r) => <span className={cn(r.arrets_non_planifies_sec > 0 && "text-red-400")}>{fmtDuree(r.arrets_non_planifies_sec)}</span> },
];

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-popover border border-border rounded-sm p-2 text-[11px] font-mono shadow-xl">
      <div className="font-semibold mb-1">{label} <span className="text-muted-foreground">{d.horaires}</span></div>
      <div className="text-emerald-400">TRS {fmtPct(d.trs)}</div>
      {payload.map((p) => <div key={p.dataKey} style={{ color: p.fill }}>{p.name} {fmtPct(p.value)}</div>)}
    </div>
  );
};

const Journee = ({ j }) => (
  <div data-testid="trs-quart-journee" className="px-3 py-2 border-t border-border bg-muted/30 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] font-mono">
    <span className="eyebrow">Journée industrielle — TRS ligne</span>
    <span>TRS <b className={cn("text-sm", TONE_TEXT[trsTone(j.trs)])}>{fmtPct(j.trs)}</b></span>
    <span className="text-muted-foreground">D <b className={TONE_TEXT[trsTone(j.disponibilite)]}>{fmtPct(j.disponibilite)}</b></span>
    <span className="text-muted-foreground">P <b className={TONE_TEXT[trsTone(j.performance)]}>{fmtPct(j.performance)}</b></span>
    <span className="text-muted-foreground">Q <b className={TONE_TEXT[trsTone(j.qualite)]}>{fmtPct(j.qualite)}</b></span>
    <span className="text-muted-foreground">Bonnes <b className="text-foreground">{fmtNum(j.bonnes)}</b> / Obj. {j.objectif ? fmtNum(j.objectif) : "—"}{j.avancement_pct != null && <> · {fmtPct(j.avancement_pct, 0)}</>}</span>
    <span className="text-muted-foreground">Rejets <b className={cn(j.rejets > 0 ? "text-amber-400" : "text-foreground")}>{fmtNum(j.rejets)}</b></span>
    <span className="text-muted-foreground">Arrêts <b className="text-foreground">{fmtDuree(j.temps_arret_sec)}</b> ({fmtDuree(j.arrets_non_planifies_sec)} non plan.)</span>
  </div>
);

export const ParQuartDetail = ({ ligneId, dateJour, refreshMs }) => {
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["trs-par-quart", ligneId, dateJour], queryFn: () => get("/trs/par-quart", { ligne_id: ligneId, date_jour: dateJour }), enabled: !!ligneId, refetchInterval: refreshMs });
  const rows = data?.par_quart || [];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-3">
      <Panel title="Découpage par quart" subtitle={data ? `${data.ligne} · ${data.date}` : undefined} testId="trs-quart-panel" bodyClassName="p-0">
        {!ligneId ? <div className="p-4 text-xs text-muted-foreground">Sélectionnez une ligne.</div>
          : isLoading ? <LoadingState rows={3} /> : error ? <ErrorState error={error} onRetry={refetch} />
          : <DataTable testId="trs-quart-table" columns={columns} rows={rows} rowKey={(r) => r.quart_id} emptyTitle="Aucun quart configuré" emptyHint="Définissez les quarts dans Admin → Quarts." />}
        {data?.journalier && <Journee j={data.journalier} />}
      </Panel>
      <Panel title="D / P / Q par quart" subtitle="seuil TRS 75 %" testId="trs-quart-chart" bodyClassName="p-2">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barCategoryGap="30%">
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="quart" tick={AXIS} axisLine={false} tickLine={false} interval={0} />
              <YAxis domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip />} cursor={{ fill: "#1e293b", opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
              <ReferenceLine y={75} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.6} />
              <Bar dataKey="disponibilite" name="D" fill="#3b82f6" />
              <Bar dataKey="performance" name="P" fill="#8b5cf6" />
              <Bar dataKey="qualite" name="Q" fill="#f59e0b" />
              <Bar dataKey="trs" name="TRS" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
};
